/** Superadmin-only Operator-Panel: READ-ONLY Shopify Zero-Delta Dry-Run + vollständige Root-Cause-
 *  Diagnose. Beide laufen über die zertifizierte Operator Bridge (Einmal-Key, apply=false).
 *   - Dry-Run:   POST /api/admin/operator/shopify-zero-delta-dry-run   (3× für Three-Run-Stability)
 *   - Diagnose:  POST /api/admin/operator/shopify-zero-delta-diagnose  (alle Delta-Orders)
 *  Der Einmal-Key bleibt nur kurz im Speicher, wird nie angezeigt/gespeichert. Strikt READ-ONLY. */
import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Play, Search, CheckCircle2, AlertTriangle, Loader2, Copy, Check, Download } from "lucide-react";

const RUNS = 3;
const DIAG_ACTION = "SHOPIFY_ZERO_DELTA_PROD_DIAGNOSE";
const APPLY_ACTION = "SHOPIFY_ZERO_DELTA_PROD_APPLY";
const APPLY_CONFIRM = "ICH-BESTAETIGE-PROD-APPLY";
const RES_DRY_ACTION = "ORPHANED_RESERVATION_RELEASE_DRY_RUN";
const RES_APPLY_ACTION = "ORPHANED_RESERVATION_RELEASE_APPLY";
const RES_APPLY_CONFIRM = "ICH-BESTAETIGE-RES-RELEASE";

async function mintAndCall(action, endpoint) {
  const mint = await api.post(`/admin/operator/keys${action ? `?action=${action}` : ""}`);
  const key = mint.data.key;
  const res = await api.post(endpoint, null, { headers: { "X-Operator-Key": key } });
  return res.data;
}

async function mintAndApply() {
  const mint = await api.post(`/admin/operator/keys?action=${APPLY_ACTION}`);
  const res = await api.post("/admin/operator/shopify-zero-delta-apply", null, {
    headers: { "X-Operator-Key": mint.data.key, "X-Operator-Confirm": APPLY_CONFIRM } });
  return res.data;
}

const money = (n) => (typeof n === "number"
  ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—");

export default function OperatorBridgeTab() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [threeRun, setThreeRun] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);
  const [error, setError] = useState("");
  const [applyRunning, setApplyRunning] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resRun, setResRun] = useState(false);
  const [resRes, setResRes] = useState(null);
  const [resErr, setResErr] = useState("");
  const [resApplyRun, setResApplyRun] = useState(false);
  const [resConfirm, setResConfirm] = useState(false);
  const [finBusy, setFinBusy] = useState("");
  const [finRes, setFinRes] = useState({});
  const [finErr, setFinErr] = useState("");
  const FIN_OPS = {
    rev1151: { label: "#1151 Umsatz +29,16 €", path: "/orders/f816fbd9-0671-4e75-9825-869996e7613f/correct-revenue-relevance", body: { revenue_relevant: true, reason: "Historische Umsatznachbuchung #1151" } },
    cogs1118: { label: "#1118 COGS −53,52 €", path: "/orders/45d0f2dd-898c-4ca4-9785-9ba1d74880e0/correct-cogs-keeper", body: null },
  };
  const runFin = async (opKey, apply) => {
    const op = FIN_OPS[opKey];
    if (apply && !window.confirm(`${op.label}: jetzt verbindlich buchen? (append-only, idempotent)`)) return;
    setFinBusy(`${opKey}-${apply ? "apply" : "dry"}`); setFinErr("");
    try {
      const r = await api.post(`${op.path}?apply=${apply}`, op.body);
      setFinRes((prev) => ({ ...prev, [opKey]: r.data }));
    } catch (e) { setFinErr(apiError(e)); } finally { setFinBusy(""); }
  };

  const runResDry = async () => {
    setResRun(true); setResErr(""); setResRes(null);
    try { setResRes(await mintAndCall(RES_DRY_ACTION, "/admin/operator/orphaned-reservation-release-dry-run")); }
    catch (e) { setResErr(apiError(e)); } finally { setResRun(false); }
  };
  const runResApply = async () => {
    setResApplyRun(true); setResErr(""); setResConfirm(false);
    try {
      const mint = await api.post(`/admin/operator/keys?action=${RES_APPLY_ACTION}`);
      const res = await api.post("/admin/operator/orphaned-reservation-release-apply", null, {
        headers: { "X-Operator-Key": mint.data.key, "X-Operator-Confirm": RES_APPLY_CONFIRM } });
      setResRes(res.data);
    } catch (e) { setResErr(apiError(e)); } finally { setResApplyRun(false); }
  };

  const run = async () => {
    setRunning(true); setError(""); setResult(null); setThreeRun(null); setDiag(null);
    try {
      const runs = [];
      for (let i = 0; i < RUNS; i++) {
        setProgress(`Lauf ${i + 1} von ${RUNS} …`);
        runs.push(await mintAndCall("", "/admin/operator/shopify-zero-delta-dry-run"));
      }
      const first = JSON.stringify(runs[0]);
      setThreeRun(runs.every((r) => JSON.stringify(r) === first) ? "PASS" : "FAIL");
      setResult(runs[0]);
    } catch (e) { setError(apiError(e)); } finally { setRunning(false); setProgress(""); }
  };

  const runDiag = async () => {
    setDiagRunning(true); setError(""); setDiag(null);
    try {
      setDiag(await mintAndCall(DIAG_ACTION, "/admin/operator/shopify-zero-delta-diagnose"));
    } catch (e) { setError(apiError(e)); } finally { setDiagRunning(false); }
  };

  const runApply = async () => {
    setApplyRunning(true); setApplyError(""); setApplyResult(null); setConfirmOpen(false);
    try {
      setApplyResult(await mintAndApply());
    } catch (e) { setApplyError(apiError(e)); } finally { setApplyRunning(false); }
  };

  const gates = result ? [
    ["Order-Delta = 0,00", result.order_delta_orders === 0, `${result.order_delta_orders} Order(s) mit Delta`],
    ["Tax-Delta = 0,00", result.tax_delta_orders === 0, `${result.tax_delta_orders} Order(s) mit Delta`],
    ["Finance-Delta = 0,00", result.finance_delta_orders === 0, `${result.finance_delta_orders} Order(s) mit Delta`],
    ["Unexplained Delta Count = 0", result.unexplained_delta_count === 0, `${result.unexplained_delta_count} unerklaert`],
    ["Revenue exactly once", result.revenue_exactly_once === "PASS", result.revenue_exactly_once],
    ["Post-Migration Re-Sync", result.post_migration_resync === "PASS", result.post_migration_resync],
    ["Three-Run-Stability", threeRun === "PASS", threeRun || "—"],
  ] : [];
  const ready = gates.length > 0 && gates.every((g) => g[1]) && result?.final_verdict === "READY FOR PO APPLY DECISION";

  const facts = result ? [
    ["Native Shopify Orders", result.native_shopify_orders],
    ["Duplicate Revenue Events", result.duplicate_events],
    ["Duplicate Economic Amount", `${money(result.duplicate_economic_amount)} €`],
    ["Geplante Reversals", result.planned_reversals],
    ["Order-Migrationen", result.order_migrations],
    ["Finance-Corrections", `${result.finance_corrections} (Σ ${money(result.finance_correction_total)} €)`],
    ["AUF-Klassifikation", `${result.auf_classification?.count ?? "—"} — ${result.auf_classification?.status ?? ""}`],
    ["Datensätze mit Fehler", result.records_with_errors ?? 0],
    ["Unvollständig (kein Snapshot)", result.incomplete_records ?? 0],
    ["PROD Writes / Shopify Writes", `${result.prod_writes ?? 0} / ${result.shopify_writes ?? 0}`],
  ] : [];

  const diagReady = diag?.final_verdict === "READY FOR PO APPLY DECISION";

  return (
    <div className="max-w-3xl space-y-4" data-testid="operator-bridge-tab">
      <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="res-release-panel">
        <div>
          <h3 className="font-heading font-medium">Verwaiste Reservierungen bereinigen (AUF-1010)</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Gibt genau die 2 hängenden offenen Reservierungen des vollständig versandten Auftrags
            AUF-1010 frei (ISOBAG_YELLOW 15 / GREEN 25). Idempotent, append-only, ohne Finanz-/
            Bestandswirkung. „Dry-Run" zeigt nur eine Vorschau; „Bereinigung ausführen" schreibt einmalig.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={runResDry} disabled={resRun || resApplyRun} data-testid="res-dry-btn">
            {resRun ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            {resRun ? "Prüfe …" : "Dry-Run / Vorschau"}
          </Button>
          <Button variant="destructive" onClick={() => setResConfirm(true)} disabled={resRun || resApplyRun} data-testid="res-apply-btn">
            {resApplyRun ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {resApplyRun ? "Bereinigung läuft …" : "Bereinigung ausführen"}
          </Button>
        </div>
        {resConfirm && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 space-y-2" data-testid="res-confirm">
            <div className="flex items-center gap-2 text-sm font-medium text-destructive"><AlertTriangle className="h-4 w-4" /> Reservierungen jetzt freigeben? (einmalig, idempotent)</div>
            <div className="flex gap-2">
              <Button variant="destructive" size="sm" onClick={runResApply} disabled={resApplyRun} data-testid="res-confirm-btn">Ja, ausführen</Button>
              <Button variant="outline" size="sm" onClick={() => setResConfirm(false)} data-testid="res-cancel-btn">Abbrechen</Button>
            </div>
          </div>
        )}
        {resErr && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="res-error">{resErr}</div>}
        {resRes && (
          <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm space-y-1" data-testid="res-result">
            <div className="flex items-center gap-2 font-medium text-primary"><CheckCircle2 className="h-4 w-4" /> {resRes.mode} — {resRes.verdict} · PROD-Writes: {resRes.prod_writes}</div>
            {resRes.reconciliation && (
              <div className="text-xs text-muted-foreground font-mono">
                reserved nachher: {JSON.stringify(resRes.reconciliation.reserved_qty_after)} · offen systemweit: {resRes.reconciliation.system_open_reservations_after} · Audit: {resRes.reconciliation.audit_logs_release_orphaned_reservation}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="fin-correction-panel">
        <div>
          <h3 className="font-heading font-medium">Historische Finanzkorrekturen (Keeper #1151 / #1118)</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Append-only, idempotent, DOCUMENTATION_ONLY (kein Lagerabgang). „Vorschau" rechnet nur;
            „Buchen" schreibt einmalig. #1142 ist bewusst NICHT enthalten (Review offen).
          </p>
        </div>
        {[["rev1151", "#1151 — fehlender Umsatz +29,16 € (kanonisch aus Shopify-Wahrheit)"],
          ["cogs1118", "#1118 — fehlende COGS −53,52 € (ex-AUF-1008, DOCUMENTATION_ONLY)"]].map(([k, desc]) => {
          const r = finRes[k];
          return (
            <div key={k} className="rounded border border-border/70 p-3 space-y-2" data-testid={`fin-op-${k}`}>
              <div className="text-sm">{desc}</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => runFin(k, false)} disabled={!!finBusy} data-testid={`fin-dry-${k}`}>
                  {finBusy === `${k}-dry` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Vorschau
                </Button>
                <Button variant="destructive" size="sm" onClick={() => runFin(k, true)} disabled={!!finBusy} data-testid={`fin-apply-${k}`}>
                  {finBusy === `${k}-apply` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Buchen
                </Button>
              </div>
              {r && (
                <div className="rounded bg-primary/5 border border-primary/30 px-3 py-2 text-xs font-mono" data-testid={`fin-result-${k}`}>
                  {r.apply ? "GEBUCHT" : "VORSCHAU"} · {r.verdict || (r.financial_impact ? "wird gebucht" : "keine Änderung")} · Delta: {r.correction_amount ?? r.proposed_amount ?? 0} € · Key: {r.correction_key}
                </div>
              )}
            </div>
          );
        })}
        {finErr && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="fin-error">{finErr}</div>}
      </div>

      <div>
        <h3 className="font-heading font-medium">Shopify Zero-Delta — PROD Dry-Run & Diagnose (READ-ONLY)</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Strikt lesend gegen die Produktionsdaten: keine Migration, keine Korrekturen, keine
          Datenänderung. „Dry-Run" prüft das Gate (3× für Stabilität); „Root-Cause-Diagnose"
          ordnet JEDER Delta-Order eine belegte Ursache zu.
        </p>
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 mt-0.5 text-primary shrink-0" />
        <span>Autorisierung per kryptographischem Einmal-Schlüssel (nur Superadmin, 15 min, Single-Use).
          Server erzwingt <span className="font-mono">apply=false</span>; 0 Writes.</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={run} disabled={running || diagRunning} data-testid="operator-run-btn">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {running ? (progress || "Läuft …") : "Dry-Run ausführen"}
        </Button>
        <Button variant="outline" onClick={runDiag} disabled={running || diagRunning} data-testid="operator-diag-btn">
          {diagRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
          {diagRunning ? "Diagnose läuft …" : "Root-Cause-Diagnose (alle Orders)"}
        </Button>
        {ready && (
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}
            disabled={running || diagRunning || applyRunning} data-testid="operator-apply-btn">
            {applyRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {applyRunning ? "PROD Apply läuft …" : "PROD Apply ausführen (einmalig)"}
          </Button>
        )}
      </div>

      {confirmOpen && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 space-y-3" data-testid="operator-apply-confirm">
          <div className="flex items-center gap-2 font-heading font-semibold text-destructive">
            <AlertTriangle className="h-5 w-5" /> Einmaliger PROD-Apply — bitte bestätigen
          </div>
          <p className="text-sm text-muted-foreground">
            Dies schreibt das zertifizierte Paket in die Produktionsdaten: Shopify-Rebuild, 217 Duplicate-Reversals,
            11 Finance-Corrections, 4 AUF→B2B, 25 Zettle-19%-Korrekturen. Append-only &amp; idempotent; der Server
            führt den Apply nur aus, wenn der erneute Dry-Run READY ist. <strong>Genau einmal ausführen.</strong>
          </p>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={runApply} disabled={applyRunning} data-testid="operator-apply-confirm-btn">
              {applyRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Ja, EINMALIGEN PROD-Apply jetzt ausführen
            </Button>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={applyRunning} data-testid="operator-apply-cancel-btn">Abbrechen</Button>
          </div>
        </div>
      )}

      {applyError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="operator-apply-error">
          <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Apply-Fehler</div>
          <div className="mt-0.5 text-xs">{applyError}</div>
        </div>
      )}

      {applyResult && (() => {
        const ok = applyResult.prod_apply === "PASS";
        const facts = [
          ["Shopify Rebuilds angewendet", String(applyResult.shopify_rebuilds_applied)],
          ["Dedup-Reversals", `${applyResult.dedup_reversals_applied} / ${applyResult.dedup_reversals_expected}`],
          ["Finance-Corrections", `${applyResult.finance_corrections_applied} / ${applyResult.finance_corrections_expected}`],
          ["AUF → B2B", `${applyResult.auf_to_b2b} / ${applyResult.auf_to_b2b_expected}`],
          ["Zettle 19%-Korrekturen", `${applyResult.zettle_vat_corrections} (Kohorte ${applyResult.zettle_orders}, complete=${String(applyResult.zettle_cohort_complete)})`],
          ["Zettle Gross-Delta", `${money(applyResult.zettle_gross_delta)} €`],
          ["PROD Writes / Shopify Writes", `${applyResult.prod_writes} / ${applyResult.shopify_writes}`],
        ];
        const aGates = [
          ["Order-Delta = 0", applyResult.order_delta === 0, String(applyResult.order_delta)],
          ["Tax-Delta = 0", applyResult.tax_delta === 0, String(applyResult.tax_delta)],
          ["Finance-Delta = 0", applyResult.finance_delta === 0, String(applyResult.finance_delta)],
          ["Unexplained = 0", applyResult.unexplained === 0, String(applyResult.unexplained)],
          ["Revenue exactly once", applyResult.revenue_exactly_once === "PASS", applyResult.revenue_exactly_once],
          ["UI #1139 = 21,01 / 3,99 / 25,00", !!applyResult.ui_proof_1139?.pass, JSON.stringify(applyResult.ui_proof_1139?.values || {})],
          ["UI normale Shopify-Order", !!applyResult.ui_proof_normal_shopify?.pass, String(applyResult.ui_proof_normal_shopify?.order_number || "—")],
          ["#1151 Lifecycle (entwurf)", !!applyResult.order_1151_lifecycle?.pass, String(applyResult.order_1151_lifecycle?.order_status || "—")],
        ];
        return (
          <div className="space-y-4" data-testid="operator-apply-result">
            <div className={`rounded-md border p-4 ${ok ? "border-primary/40 bg-primary/5" : "border-destructive/50 bg-destructive/10"}`} data-testid="operator-apply-verdict">
              <div className="flex items-center gap-2">
                {ok ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <AlertTriangle className="h-5 w-5 text-destructive" />}
                <span className={`font-heading font-semibold ${ok ? "text-primary" : "text-destructive"}`}>
                  {ok ? applyResult.final_status : `PROD APPLY = FAIL${applyResult.reason ? ` (${applyResult.reason})` : ""}`}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {ok ? "Historischer Cleanup abgeschlossen. Idempotent — ein erneuter Klick erzeugt 0 weitere Writes."
                    : "Fail-safe: kein zweiter Apply. Bitte Response prüfen."}
              </p>
            </div>
            <Table title="Angewendete Maßnahmen" rows={facts} />
            <Table title="Post-Apply-Gates" rows={aGates.map(([l, g, d]) => [l, String(d), g])} gate />
            <Raw data={applyResult} testid="operator-apply-raw-json" />
          </div>
        );
      })()}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="operator-error">
          <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Fehler</div>
          <div className="mt-0.5 text-xs">{error}</div>
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="operator-result">
          <div className={`rounded-md border p-4 ${ready ? "border-primary/40 bg-primary/5" : "border-chart-4/50 bg-chart-4/10"}`} data-testid="operator-verdict">
            <div className="flex items-center gap-2">
              {ready ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <AlertTriangle className="h-5 w-5 text-chart-4" />}
              <span className={`font-heading font-semibold ${ready ? "text-primary" : "text-chart-4"}`}>
                {ready ? "SHOPIFY HISTORICAL ZERO-DELTA MIGRATION — READY FOR PROD APPLY" : "HOLD — Gate nicht erfüllt"}
              </span>
            </div>
            {ready && <p className="mt-1.5 text-xs text-muted-foreground">Alle Gates erfüllt. HARD STOP: Noch KEIN Apply ohne separate PO-Freigabe.</p>}
          </div>
          <Table title="Entscheidungsgate" rows={gates.map(([l, ok, d]) => [l, String(d), ok])} gate />
          <Table title="Kennzahlen" rows={facts.map(([l, v]) => [l, String(v)])} />
          <Raw data={result} testid="operator-raw-json" />
        </div>
      )}

      {diag && (
        <div className="space-y-4" data-testid="operator-diag-result">
          <div className={`rounded-md border p-4 ${diagReady ? "border-primary/40 bg-primary/5" : "border-chart-4/50 bg-chart-4/10"}`} data-testid="operator-diag-verdict">
            <div className="flex items-center gap-2">
              {diagReady ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <AlertTriangle className="h-5 w-5 text-chart-4" />}
              <span className={`font-heading font-semibold ${diagReady ? "text-primary" : "text-chart-4"}`}>
                Root-Cause-Diagnose — {diag.final_verdict}
              </span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {diag.total_native_orders} native Orders · {diag.snapshot_orders} mit Snapshot · {diag.auf_orders} AUF.
              Unexplained: {diag.unexplained_delta_count} · Three-Run-stabil (deterministisch).
            </p>
          </div>

          {diag.shopify_sales_truth_rebuild && (() => {
            const rb = diag.shopify_sales_truth_rebuild;
            const rbReady = rb.final_verdict === "READY FOR PROD APPLY DECISION";
            return (
              <div className="space-y-3" data-testid="operator-rebuild">
                <div className={`rounded-md border p-4 ${rbReady ? "border-primary/40 bg-primary/5" : "border-chart-4/50 bg-chart-4/10"}`} data-testid="operator-rebuild-verdict">
                  <div className="flex items-center gap-2">
                    {rbReady ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <AlertTriangle className="h-5 w-5 text-chart-4" />}
                    <span className={`font-heading font-semibold ${rbReady ? "text-primary" : "text-chart-4"}`}>
                      Shopify Sales Truth — Rebuild · {rb.final_verdict}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Rekonstruktion direkt aus nativer Shopify-Evidenz (Variant/SKU + originalUnitPrice/discountAllocations/taxLines). Evidenz verfügbar: {rb.evidence_available ? "ja" : "nein (HOLD)"}.
                  </p>
                </div>
                <Table title="Shopify Sales Truth — Kennzahlen" rows={[
                  ["Total native Shopify Orders", String(rb.total_native_shopify_orders)],
                  ["AUTO-REBUILDABLE", String(rb.auto_rebuildable)],
                  ["FINANCE CORRECTION REQUIRED", `${rb.finance_correction_required} (Σ ${money(rb.finance_correction_total)} €)`],
                  ["MANUAL REVIEW REQUIRED", String(rb.manual_review_required)],
                  ["UNEXPLAINED", String(rb.unexplained)],
                  ["AUF (Channel, revenue-neutral)", String(rb.auf_channel_orders)],
                ]} />
                {(rb.manual_review_list || []).length > 0 && (
                  <details className="rounded-md border border-border bg-card" open>
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Manual-Review-Liste — PO-Entscheidung nötig ({rb.manual_review_list.length})</summary>
                    <div className="max-h-[420px] overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0"><tr>
                          {["Order", "Produkt(e)", "Variant/SKU", "Shopify-Preis", "Rabatt", "Steuer", "Problem"].map((h) => (
                            <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>))}
                        </tr></thead>
                        <tbody data-testid="operator-rebuild-manual">
                          {rb.manual_review_list.map((o, i) => (
                            <tr key={o.order_number || i} className="border-t border-border align-top">
                              <td className="px-2 py-1 font-mono">{o.order_number || "—"}</td>
                              <td className="px-2 py-1">{(o.products || []).map((p) => p.title).join(", ") || "—"}</td>
                              <td className="px-2 py-1 font-mono">{(o.products || []).map((p) => p.sku || p.variant_id).join(", ") || "—"}</td>
                              <td className="px-2 py-1 text-right font-mono">{(o.products || []).map((p) => money(p.shopify_price)).join(", ") || "—"}</td>
                              <td className="px-2 py-1 text-right font-mono">{(o.products || []).map((p) => money(p.discount)).join(", ") || "—"}</td>
                              <td className="px-2 py-1 text-right font-mono">{(o.products || []).map((p) => money(p.tax)).join(", ") || "—"}</td>
                              <td className="px-2 py-1 text-chart-4">{o.problem}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            );
          })()}

          <Table title="Root-Cause-Histogramm" head={["Ursache", "Anzahl", "Order-Δ Σ (€)", "Finance-Δ Σ (€)"]}            rows={(diag.root_cause_histogram || []).map((h) => [h.root_cause, String(h.count), money(h.order_delta_sum), money(h.finance_delta_sum)])} />

          <Table title="Normalisierungs-Strategie" rows={Object.entries(diag.normalization_strategy || {}).map(([k, v]) => [k, String(v)])} />

          <Table title="Fixierte Simulation (evidenzbasiert)" rows={[
            ["Order-Delta (Orders)", String(diag.simulated_fixed_state?.order_delta_orders)],
            ["Tax-Delta (Orders)", String(diag.simulated_fixed_state?.tax_delta_orders)],
            ["Finance-Delta (Orders)", String(diag.simulated_fixed_state?.finance_delta_orders)],
            ["Positions-Fidelity-Lücken (Shopify r/o nötig)", String(diag.simulated_fixed_state?.line_fidelity_gaps)],
            ["Unexplained (UNKNOWN + Rest)", String(diag.unexplained_delta_count)],
            ["Post-Migration Re-Sync", diag.post_migration_resync],
          ]} />

          <Table title="7 Finance-Corrections — Erklärung" rows={[
            ["Fälle mit Finance-Delta (vor)", String(diag.known_finance_corrections?.finance_delta_cases)],
            ["durch De-Dup neutralisiert", String(diag.known_finance_corrections?.absorbed_by_dedup)],
            ["nach De-Dup noch erforderlich", String(diag.known_finance_corrections?.still_required_after_dedup)],
            ["Correction-Summe (€)", money(diag.known_finance_corrections?.correction_total)],
          ]} />

          <Table title="Finance-Delta-Auflösung (nach De-Dup)" rows={[
            ["Finance-Delta VOR De-Dup (Σ €)", money(diag.finance_resolution?.finance_delta_before_total)],
            ["durch De-Dup neutralisiert (Σ €)", money(diag.finance_resolution?.neutralized_by_dedup_total)],
            ["Finance-Delta NACH De-Dup (Σ €)", money(diag.finance_resolution?.finance_delta_after_total)],
            ["Residuale Finance-Orders", String(diag.finance_resolution?.residual_orders)],
            ["davon bewiesen correctable", `${diag.finance_resolution?.remaining_corrections} (Σ ${money(diag.finance_resolution?.remaining_correction_total)} €)`],
            ["davon UNKNOWN/HOLD", String(diag.finance_resolution?.remaining_hold)],
          ]} />

          {(diag.finance_resolution?.finance_class_histogram || []).length > 0 && (
            <Table title="Finance-Root-Cause-Histogramm" head={["Finance-Klasse", "Anzahl", "Δ Σ (€)"]}
              rows={diag.finance_resolution.finance_class_histogram.map((h) => [h.finance_class, String(h.count), money(h.delta_sum)])} />
          )}

          {(diag.finance_resolution?.detail || []).length > 0 && (
            <details className="rounded-md border border-border bg-card" open>
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Residuale Finance-Deltas — Detail ({diag.finance_resolution.detail.length})</summary>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0"><tr>
                    {["Order", "Finance-Klasse", "exakt Δ", "correctable", "Goods-Rev", "Ship-Rev", "Refund", "Exclusion", "erwartet→sim"].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>))}
                  </tr></thead>
                  <tbody data-testid="operator-finance-detail">
                    {diag.finance_resolution.detail.map((o, i) => (
                      <tr key={o.order_id || i} className="border-t border-border">
                        <td className="px-2 py-1 font-mono">{o.order_number || "—"}</td>
                        <td className="px-2 py-1">{o.classification}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.exact_delta)}</td>
                        <td className="px-2 py-1">{o.correctable ? "ja" : "HOLD"}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.revenue_goods)}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.revenue_shipping)}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.refunds)}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.exclusions)}</td>
                        <td className="px-2 py-1 text-right font-mono">{money(o.expected_finance_net)}→{money(o.simulated_finance_net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <Table title="Duplicate Revenue — Verifikation" rows={[
            ["Duplicate Events", String(diag.duplicate_revenue?.duplicate_events)],
            ["Duplicate Amount (€)", money(diag.duplicate_revenue?.duplicate_economic_amount)],
            ["Geplante Reversals", String(diag.duplicate_revenue?.planned_reversals)],
            ["Revenue exactly once", diag.duplicate_revenue?.revenue_exactly_once],
          ]} />

          <Table title="Positions-Fidelity (Shopify read-only Evidenz)" rows={[
            ["Evidenz verfügbar", diag.position_fidelity?.evidence_available ? "ja" : "nein (HOLD)"],
            ["Gap-Orders (Rabatt/Mixed-Tax)", String(diag.position_fidelity?.gap_orders)],
            ["penny-genau aufgelöst", String(diag.position_fidelity?.resolved)],
            ["ungelöst (HOLD)", String(diag.position_fidelity?.unresolved)],
          ]} />

          {(diag.position_fidelity?.detail || []).length > 0 && (
            <details className="rounded-md border border-border bg-card">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Positions-Fidelity — Detail ({diag.position_fidelity.detail.length})</summary>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0"><tr>
                    {["Order", "Evidenz", "aufgelöst", "rekonstr. Netto", "rekonstr. Steuer", "nativ Netto", "Δ Netto", "Δ Steuer"].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>))}
                  </tr></thead>
                  <tbody data-testid="operator-posfidelity-detail">
                    {diag.position_fidelity.detail.map((o, i) => (
                      <tr key={o.order_id || i} className="border-t border-border">
                        <td className="px-2 py-1 font-mono">{o.order_number || "—"}</td>
                        <td className="px-2 py-1">{o.evidence}</td>
                        <td className="px-2 py-1">{o.resolved ? "ja" : "HOLD"}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.reconstructed_goods_net != null ? money(o.reconstructed_goods_net) : "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.reconstructed_goods_tax != null ? money(o.reconstructed_goods_tax) : "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.native_goods_net != null ? money(o.native_goods_net) : "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.delta_net != null ? money(o.delta_net) : (o.reason || "—")}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.delta_tax != null ? money(o.delta_tax) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <details className="rounded-md border border-border bg-card">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Vollständige Orderliste ({(diag.orders || []).length})</summary>
            <div className="max-h-[460px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0"><tr>
                  {["Order", "Snapshot", "Primär-Klasse", "Strategie", "Order-Δ", "Tax-Δ", "Finance-Δ (vor→nach Dedup)"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>))}
                </tr></thead>
                <tbody data-testid="operator-diag-orders">
                  {(diag.orders || []).map((o, i) => (
                    <tr key={o.order_id || i} className="border-t border-border">
                      <td className="px-2 py-1 font-mono">{o.order_number || "—"}</td>
                      <td className="px-2 py-1">{o.has_snapshot ? "ja" : "nein"}</td>
                      <td className="px-2 py-1">{o.primary_class}</td>
                      <td className="px-2 py-1">{o.strategy}</td>
                      <td className="px-2 py-1 text-right font-mono">{o.before_delta ? money(o.before_delta.order_delta) : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{o.before_delta ? money(o.before_delta.tax_delta) : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{o.dedup ? `${money(o.dedup.finance_delta_before)} → ${money(o.dedup.finance_delta_after)}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          <Raw data={diag} testid="operator-diag-raw-json" />
        </div>
      )}
    </div>
  );
}

const Table = ({ title, rows, head, gate }) => (
  <div className="rounded-md border border-border bg-card overflow-hidden">
    <div className="bg-muted/50 px-3 py-2 text-sm font-medium">{title}</div>
    <table className="w-full text-sm">
      {head && <thead><tr className="text-xs text-muted-foreground">{head.map((h) => <th key={h} className="px-3 py-1.5 text-left font-medium">{h}</th>)}</tr></thead>}
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-border">
            {r.slice(0, gate ? 2 : r.length).map((c, j) => (
              <td key={j} className={`px-3 py-2 ${j === 0 ? "" : "text-right font-mono text-xs text-muted-foreground"}`}>{c}</td>
            ))}
            {gate && <td className="px-3 py-2 w-10 text-right">{r[2] ? <CheckCircle2 className="h-4 w-4 text-primary inline" /> : <AlertTriangle className="h-4 w-4 text-destructive inline" />}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Raw = ({ data, testid }) => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(json);
      else {
        const ta = document.createElement("textarea");
        ta.value = json; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };
  const download = () => {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${testid || "diagnose"}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  return (
    <details className="rounded-md border border-border bg-card">
      <summary className="cursor-pointer px-3 py-2 text-sm text-muted-foreground flex items-center justify-between gap-2">
        <span>Rohdaten (JSON)</span>
        <span className="flex items-center gap-1.5" onClick={(e) => e.preventDefault()}>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={copy} data-testid={`${testid}-copy`}>
            {copied ? <Check className="h-3.5 w-3.5 mr-1 text-primary" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
            {copied ? "Kopiert" : "Kopieren"}
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={download} data-testid={`${testid}-download`}>
            <Download className="h-3.5 w-3.5 mr-1" /> JSON
          </Button>
        </span>
      </summary>
      <pre className="px-3 py-2 text-xs overflow-x-auto max-h-[400px]" data-testid={testid}>{json}</pre>
    </details>
  );
};
